import React, { useEffect, useState, useCallback } from "react";
import { API } from "./cache";
import "./EffortEstimatePanel.css";

function fmtHM(h, m) {
  const hours = parseInt(h, 10) || 0;
  const mins = parseInt(m, 10) || 0;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  if (mins > 0) return `${mins}m`;
  return "0m";
}

function pct(actual, est) {
  if (!est || est === 0) return 0;
  const p = Math.round((actual / est) * 100);
  return p;
}

function totalMinutes(h, m) {
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}-${mm}-${yy}`;
}

function varianceLabel(estMins, actMins) {
  const diff = actMins - estMins;
  if (diff === 0) return "0m";
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const formatted = fmtHM(h, m);
  return diff > 0 ? `+${formatted}` : `-${formatted}`;
}

const PHASE_OPTIONS = [
  "design",
  "development",
  "testing",
  "review",
  "deployment",
  "documentation",
  "other",
];


const EMPTY_FORM = {
  estimated_hours: 0,
  estimated_minutes: 0,
  phase: "",
  notes: "",
  revision_notes: "",
};

export default function EffortEstimatePanel({ refType, refId }) {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState(null);
  const [revLoading, setRevLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const fetchEstimate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API}/effort-estimates?ref_type=${refType}&ref_id=${refId}`,
        { credentials: "include" }
      );
      if (res.status === 404) {
        setEstimate(null);
      } else if (res.ok) {
        const data = await res.json();
        setEstimate(data);
      } else {
        setError("Failed to load estimate.");
      }
    } catch {
      setError("Network error loading estimate.");
    } finally {
      setLoading(false);
    }
  }, [refType, refId]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, estimated_hours: 2, estimated_minutes: 0 });
    setEditMode(true);
  };

  const openEdit = () => {
    if (!estimate) return;
    setForm({
      estimated_hours: estimate.estimated_hours ?? 0,
      estimated_minutes: estimate.estimated_minutes ?? 0,
      phase: estimate.phase ?? "",
      notes: estimate.notes ?? "",
      revision_notes: "",
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setError("");
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const hrs  = parseInt(form.estimated_hours,  10) || 0;
    const mins = parseInt(form.estimated_minutes, 10) || 0;
    if (!form.phase) { setError("Phase is required."); return; }
    if (hrs < 0 || hrs > 160) { setError("Hours must be a whole number between 0 and 160."); return; }
    if (mins < 0 || mins > 59) { setError("Minutes must be a whole number between 0 and 59."); return; }

    setSaving(true);
    setError("");
    try {
      let res;
      if (estimate) {
        res = await fetch(`${API}/effort-estimates/${estimate.id}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            estimated_hours: hrs,
            estimated_minutes: mins,
            phase: form.phase,
            notes: form.notes,
            revision_notes: form.revision_notes,
          }),
        });
      } else {
        res = await fetch(`${API}/effort-estimates`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ref_type: refType,
            ref_id: refId,
            estimated_hours: hrs,
            estimated_minutes: mins,
            phase: form.phase,
            notes: form.notes,
          }),
        });
      }
      if (res.ok) {
        const data = await res.json();
        setEstimate(data);
        setEditMode(false);
        setRevisions(null);
        setShowHistory(false);
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || "Failed to save estimate.");
      }
    } catch {
      setError("Network error saving estimate.");
    } finally {
      setSaving(false);
    }
  };

  const handleShowHistory = async () => {
    if (!estimate) return;
    if (showHistory) {
      setShowHistory(false);
      return;
    }
    setShowHistory(true);
    if (revisions !== null) return;
    setRevLoading(true);
    try {
      const res = await fetch(
        `${API}/effort-estimates/${estimate.id}/revisions`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setRevisions(data);
      } else {
        setRevisions([]);
      }
    } catch {
      setRevisions([]);
    } finally {
      setRevLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="ee-wrap">
        <div className="ee-loading">Loading estimate...</div>
      </div>
    );
  }

  const estMins = estimate
    ? totalMinutes(estimate.estimated_hours, estimate.estimated_minutes)
    : 0;
  const actMins = estimate
    ? totalMinutes(estimate.actual_hours ?? 0, estimate.actual_minutes ?? 0)
    : 0;
  const percentage = estMins > 0 ? pct(actMins, estMins) : 0;
  const isOver = actMins > estMins;
  const isUnder = actMins < estMins && estimate !== null;
  const fillPct = Math.min(percentage, 100);
  const revCount =
    estimate && estimate.revision_count != null ? estimate.revision_count : 0;

  return (
    <div className="ee-wrap">
      <div className="ee-header">
        <span className="ee-title">EFFORT ESTIMATE</span>
        {estimate && !editMode && (
          <button className="ee-edit-btn" onClick={openEdit}>
            Edit
          </button>
        )}
      </div>

      {error && <div className="ee-error">{error}</div>}

      {!estimate && !editMode && (
        <div className="ee-empty">
          No estimate set yet.
          <br />
          <button className="ee-btn ee-btn--primary ee-set-btn" onClick={openCreate}>
            Set Estimate
          </button>
        </div>
      )}

      {estimate && !editMode && (
        <>
          <div className="ee-stats">
            <div className="ee-stat">
              <span className="ee-stat-label">Estimated</span>
              <span className="ee-stat-value">
                {fmtHM(estimate.estimated_hours, estimate.estimated_minutes)}
              </span>
            </div>
            <div className="ee-stat">
              <span className="ee-stat-label">Actual (daily reports)</span>
              <span className="ee-stat-value">
                {fmtHM(estimate.actual_hours ?? 0, estimate.actual_minutes ?? 0)}
              </span>
            </div>
            <div className="ee-stat">
              <span className="ee-stat-label">Variance</span>
              <span
                className={
                  "ee-stat-value" +
                  (isOver ? " ee-stat--over" : isUnder ? " ee-stat--under" : "")
                }
              >
                {varianceLabel(estMins, actMins)}
              </span>
            </div>
          </div>

          <div className="ee-progress-row">
            <div className="ee-progress-bar">
              <div
                className="ee-progress-fill"
                style={{
                  width: `${fillPct}%`,
                  background: isOver ? "#c62828" : "#1565c0",
                }}
              />
            </div>
            <span className="ee-progress-pct">
              {percentage}%{isOver ? " — over estimate" : ""}
            </span>
            {estimate.phase && (
              <span className="ee-phase-tag">{estimate.phase}</span>
            )}
          </div>

          {estimate.notes && (
            <div className="ee-notes">Notes: {estimate.notes}</div>
          )}

          <button className="ee-history-toggle" onClick={handleShowHistory}>
            {showHistory ? "▼" : "▶"} {showHistory ? "Hide" : "Show"} revision
            history{revCount > 0 ? ` (${revCount})` : ""}
          </button>

          {showHistory && (
            <div className="ee-revision-list">
              {revLoading && (
                <div className="ee-loading">Loading history...</div>
              )}
              {!revLoading && revisions && revisions.length === 0 && (
                <div className="ee-empty">No revision history yet.</div>
              )}
              {!revLoading &&
                revisions &&
                revisions.map((rev, idx) => (
                  <div className="ee-revision-item" key={rev.id ?? idx}>
                    <span>
                      {fmtHM(rev.prev_hours, rev.prev_minutes)}
                      {" → "}
                      {fmtHM(rev.new_hours, rev.new_minutes)}
                    </span>
                    <span className="ee-rev-sep">·</span>
                    <span>{rev.revised_by_name}</span>
                    {rev.revision_notes && (
                      <>
                        <span className="ee-rev-sep">·</span>
                        <span>{rev.revision_notes}</span>
                      </>
                    )}
                    <span className="ee-rev-sep">·</span>
                    <span>{fmtDate(rev.revised_at)}</span>
                  </div>
                ))}
            </div>
          )}
        </>
      )}

      {editMode && (
        <div className="ee-form">
          <div className="ee-form-row">
            <div className="ee-form-group">
              <label className="ee-form-label">Phase</label>
              <select
                className="ee-form-select"
                value={form.phase}
                onChange={(e) => handleFormChange("phase", e.target.value)}
              >
                <option value="" disabled>— Select phase —</option>
                {PHASE_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="ee-form-group">
              <label className="ee-form-label">Estimated Hours <span style={{fontWeight:400,color:"#aaa"}}>(0–160)</span></label>
              <input
                type="number"
                min={0}
                max={160}
                step={1}
                className="ee-form-input"
                value={form.estimated_hours}
                onChange={(e) =>
                  handleFormChange("estimated_hours", Math.min(160, Math.max(0, parseInt(e.target.value) || 0)))
                }
              />
            </div>
            <div className="ee-form-group">
              <label className="ee-form-label">Estimated Minutes <span style={{fontWeight:400,color:"#aaa"}}>(0–59)</span></label>
              <input
                type="number"
                min={0}
                max={59}
                step={1}
                className="ee-form-input"
                value={form.estimated_minutes}
                onChange={(e) =>
                  handleFormChange("estimated_minutes", Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))
                }
              />
            </div>
          </div>

          <div className="ee-form-group">
            <label className="ee-form-label">Notes</label>
            <textarea
              className="ee-form-textarea"
              rows={3}
              value={form.notes}
              onChange={(e) => handleFormChange("notes", e.target.value)}
            />
          </div>

          {estimate && (
            <div className="ee-form-group">
              <label className="ee-form-label">Revision Notes</label>
              <input
                type="text"
                className="ee-form-input"
                placeholder="Why is this estimate changing?"
                value={form.revision_notes}
                onChange={(e) =>
                  handleFormChange("revision_notes", e.target.value)
                }
              />
            </div>
          )}

          <div className="ee-form-actions">
            <button
              className="ee-btn ee-btn--ghost"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="ee-btn ee-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
